import { Color } from 'three'
import NodeMaterial from './material/NodeMaterial'
import NodeMirrorMaterial from './material/NodeMirrorMaterial'
import getParticles from './utils/getParticles'
import expect from './utils/expect'

export const POINT_SCALE = 0.009

const HIGHLIGHT_SCALE = 1.25

export const Y_POS = 0.0125

const HIGHLIGHT_Y = 0.01

export const PARTICLE_COLOR = new Color(0xc97344)

const SELECT_COLOR = new Color(0x3333ff)

const HIGHLIGHT_COLOR = new Color(0xf48924)

const ACTIVE_VALUE = 1

const DEACTIVE_VALUE = 0

const CONNECTED_COLOR = new Color(0xe0500d)

export default function Particles(camera, { onBeforeQualityChange, onAfterQualityChange, onIntroDone }) {
    let introDone = false

    const particleMaterial = new NodeMaterial().setConnectedColor(CONNECTED_COLOR)

    this.getParticleMaterial = () => (
        particleMaterial
    )

    const mirrorMaterial = new NodeMirrorMaterial().setConnectedColor(CONNECTED_COLOR)

    const activePoints = new WeakMap()

    let currentTime = 0

    let introTime = 100

    let coordinates = []

    let qualityLevel

    let highQualityParticle = null

    let lowQualityParticle = null

    let particle = null

    let positions

    const updatePoint = (point, attrNames, eachFn, { needsUpdate = true } = {}) => {
        expect(particle, 'Expected `particle` to be defined. Forgot to call `setQuality`?')

        const { piece, instance, verticeSize } = particle

        const attrs = attrNames.map((n) => instance.geometry.attributes[n])

        if (typeof eachFn === 'function') {
            const c = positions.get(point) * verticeSize
            const attribCount = piece.attributes.position.count

            for (let i = 0; i < attribCount; i++) {
                const cx = c + (i * 4)

                eachFn(cx, attrs)
            }
        }

        if (needsUpdate) {
            attrs.forEach((attr) => {
                Object.assign(attr, {
                    needsUpdate: true,
                })
            })
        }
    }

    const resetAttributes = (time) => {
        coordinates.forEach((point) => {
            updatePoint(point, ['startPos', 'endPos', 'extra', 'color'], (i, [startPos, endPos, extra, color]) => {
                Object.assign(startPos.array, {
                    [i]: point.x,
                    [i + 1]: Y_POS,
                    [i + 2]: point.z,
                    [i + 3]: time,
                })

                Object.assign(endPos.array, {
                    [i]: point.x,
                    [i + 1]: Y_POS,
                    [i + 2]: point.z,
                    [i + 3]: 2.0, // speed
                })

                Object.assign(extra.array, {
                    [i]: POINT_SCALE,
                    [i + 1]: POINT_SCALE,
                    [i + 2]: DEACTIVE_VALUE,
                    [i + 3]: time,
                })

                Object.assign(color.array, {
                    [i]: PARTICLE_COLOR.r,
                    [i + 1]: PARTICLE_COLOR.g,
                    [i + 2]: PARTICLE_COLOR.b,
                    [i + 3]: 0,
                })
            })
        })
    }

    this.setQuality = (level, { reset = true } = {}) => {
        if (level === qualityLevel) {
            return
        }

        if (typeof onBeforeQualityChange === 'function') {
            onBeforeQualityChange(particle)
        }

        if (level > 0) {
            if (!highQualityParticle) {
                highQualityParticle = getParticles(coordinates.length, particleMaterial, mirrorMaterial, {
                    highQuality: true,
                })
            }

            particle = highQualityParticle
        } else {
            if (!lowQualityParticle) {
                lowQualityParticle = getParticles(coordinates.length, particleMaterial, mirrorMaterial, {
                    highQuality: false,
                })
            }

            particle = lowQualityParticle
        }

        if (reset) {
            resetAttributes(currentTime)
        }

        if (typeof onAfterQualityChange === 'function') {
            onAfterQualityChange(particle)
        }

        qualityLevel = level
    }

    this.dehighlight = (point, time) => {
        updatePoint(point, ['color', 'startPos', 'endPos', 'extra'], (i, [color, startPos, endPos, extra]) => {
            Object.assign(color.array, {
                [i]: PARTICLE_COLOR.r,
                [i + 1]: PARTICLE_COLOR.g,
                [i + 2]: PARTICLE_COLOR.b,
                [i + 3]: 0,
            })

            Object.assign(startPos.array, {
                [i + 1]: Y_POS + HIGHLIGHT_Y,
                [i + 3]: time,
            })

            Object.assign(endPos.array, {
                [i + 1]: Y_POS,
                [i + 3]: 5.0,
            })

            Object.assign(extra.array, {
                [i]: POINT_SCALE,
                [i + 1]: POINT_SCALE * HIGHLIGHT_SCALE,
                [i + 3]: time,
            })
        })
    }

    this.highlight = (point, time) => {
        updatePoint(point, ['color', 'startPos', 'endPos', 'extra'], (i, [color, startPos, endPos, extra]) => {
            Object.assign(color.array, {
                [i]: HIGHLIGHT_COLOR.r,
                [i + 1]: HIGHLIGHT_COLOR.g,
                [i + 2]: HIGHLIGHT_COLOR.b,
                [i + 3]: 0,
            })

            Object.assign(startPos.array, {
                [i + 1]: Y_POS,
                [i + 3]: time,
            })

            Object.assign(endPos.array, {
                [i + 1]: Y_POS + HIGHLIGHT_Y,
                [i + 3]: 5.0,
            })

            Object.assign(extra.array, {
                [i]: POINT_SCALE * HIGHLIGHT_SCALE,
                [i + 1]: POINT_SCALE,
                [i + 3]: time,
            })
        })
    }

    this.down = (point, time) => {
        updatePoint(point, ['color', 'startPos', 'endPos'], (i, [color, startPos, endPos]) => {
            Object.assign(color.array, {
                [i]: HIGHLIGHT_COLOR.r,
                [i + 1]: HIGHLIGHT_COLOR.g,
                [i + 2]: HIGHLIGHT_COLOR.b,
            })

            Object.assign(startPos.array, {
                [i + 1]: Y_POS + HIGHLIGHT_Y,
                [i + 3]: time,
            })

            Object.assign(endPos.array, {
                [i + 1]: Y_POS - 0.005,
                [i + 3]: 5.0,
            })
        })
    }

    this.select = (point, time) => {
        updatePoint(point, ['color', 'endPos', 'extra'], (i, [color, endPos, extra]) => {
            Object.assign(endPos.array, {
                [i + 1]: Y_POS,
                [i + 3]: 8.0,
            })

            Object.assign(extra.array, {
                [i]: POINT_SCALE * 1.0, // POINT_SCALE * 1.2
                [i + 1]: POINT_SCALE * 0.5,
                [i + 2]: DEACTIVE_VALUE,
                [i + 3]: time,
            })

            Object.assign(color.array, {
                [i]: SELECT_COLOR.r,
                [i + 1]: SELECT_COLOR.g,
                [i + 2]: SELECT_COLOR.b,
                [i + 3]: 0,
            })
        })
    }

    this.deselect = (point, time) => {
        updatePoint(point, ['extra'], (i, [extra]) => {
            Object.assign(extra.array, {
                [i]: POINT_SCALE * 0.75,
                [i + 1]: POINT_SCALE * 1.0,
                [i + 2]: DEACTIVE_VALUE,
                [i + 3]: time,
            })
        })

        setTimeout(() => {
            const isActive = activePoints.get(point)

            updatePoint(point, ['color', 'extra'], (i, [color, extra]) => {
                Object.assign(extra.array, {
                    [i]: POINT_SCALE * 1.0,
                    [i + 1]: POINT_SCALE * 0.75,
                    [i + 2]: Number(!!isActive),
                    [i + 3]: currentTime,
                })

                Object.assign(color.array, {
                    [i]: PARTICLE_COLOR.r,
                    [i + 1]: PARTICLE_COLOR.g,
                    [i + 2]: PARTICLE_COLOR.b,
                    [i + 3]: 0,
                })
            })
        }, 350)
    }

    this.active = (point) => {
        activePoints.set(point, true)

        updatePoint(point, ['extra'], (i, [extra]) => {
            Object.assign(extra.array, {
                [i + 2]: ACTIVE_VALUE,
            })
        })
    }

    this.deactive = (point) => {
        activePoints.delete(point)

        updatePoint(point, ['extra'], (i, [extra]) => {
            Object.assign(extra.array, {
                [i + 2]: DEACTIVE_VALUE,
            })
        })
    }

    let ready = false

    let introTimes

    this.setCoordinates = (_coordinates, timing) => {
        ready = false

        coordinates = [..._coordinates]

        introTimes = new WeakMap()

        positions = new WeakMap()

        this.setQuality(1, {
            reset: false,
        })

        const { quantity } = particle

        coordinates.forEach((point, position) => {
            introTime = currentTime + 1.0 + timing.get(point)

            introTimes.set(point, introTime)

            positions.set(point, position % quantity)

            updatePoint(point, ['color', 'startPos', 'endPos', 'extra'], (i, [color, startPos, endPos, extra]) => {
                Object.assign(startPos.array, {
                    [i]: point.x,
                    [i + 1]: 0.15,
                    [i + 2]: point.z,
                    [i + 3]: introTime,
                })

                Object.assign(endPos.array, {
                    [i]: point.x,
                    [i + 1]: Y_POS,
                    [i + 2]: point.z,
                    [i + 3]: 2.0, // speed
                })

                Object.assign(extra.array, {
                    [i]: POINT_SCALE,
                    [i + 1]: 0,
                    [i + 2]: DEACTIVE_VALUE,
                    [i + 3]: introTime,
                })

                Object.assign(color.array, {
                    [i]: PARTICLE_COLOR.r,
                    [i + 1]: PARTICLE_COLOR.g,
                    [i + 2]: PARTICLE_COLOR.b,
                    [i + 3]: 0,
                })
            }, {
                // Postpone the update till we got all the points in.
                needsUpdate: false,
            })
        })

        introTime += 0.6

        // Sort to get better hover and click
        coordinates.sort((a, b) => b.z - a.z)

        updatePoint(null, ['color', 'startPos', 'endPos', 'extra'], null)

        ready = true
    }

    this.getCoordinates = () => (
        coordinates
    )

    this.update = (time) => {
        currentTime = time

        if (!ready) {
            // `update` can happend before all the points are in. Let's prevent that.
            return
        }

        particleMaterial.setTime(time)
        mirrorMaterial.setTime(time)

        if (!introDone && time > introTime) {
            onIntroDone()
            introDone = true
        }
    }

    this.isIntroDone = (time, { point = null } = {}) => {
        if (point) {
            // +1s because we take the estimated duration into account.
            return (introTimes.get(point) || Number.POSITIVE_INFINITY) + 1.0 < time
        }

        return introTime < time
    }
}
